import { useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * "Go live" dialog (M31 test-channel completion).
 *
 * Rendered from the dashboard's test-mode banner (admin only). Going live:
 *   1. (optional, default on) archives every HubSpot Deal + line item the
 *      app created (ampdeck_stripe_synced='true') — soft delete, 90-day trash;
 *   2. clears AmpDeck's synced test data (roles, settings, audit, pipelines stay);
 *   3. disconnects the test Stripe account.
 *
 * On success the SPA hard-navigates to `/` — RootRedirect sees
 * stripeConnected=false and sends the tenant into the wizard, whose primary
 * path is the LIVE Stripe connect. A fresh live import then rebuilds the
 * dashboards on real data.
 */
export function GoLiveDialog({ onClose }: { onClose: () => void; }) {
  const [deleteRecords, setDeleteRecords] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('onboarding/go-live', { deleteHubSpotRecords: deleteRecords });
      // Hard navigation — the SPA state (connection, principal caches) is
      // intentionally discarded; RootRedirect re-derives everything from
      // /onboarding/status (now disconnected → wizard).
      window.location.assign('/');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to go live — try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="go-live-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="card w-full max-w-lg p-6">
        <h2 id="go-live-title" className="text-lg font-semibold text-ink-900">
          Go live with real Stripe data
        </h2>
        <p className="mt-2 text-sm text-ink-600">
          This disconnects your test Stripe account and clears the synced test
          data, so you can connect your live account and import real data.
          Your team roles, settings, pipelines and audit log are kept.
        </p>

        <label className="mt-4 flex items-start gap-3 rounded-lg bg-ink-50 p-4 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={deleteRecords}
            onChange={(e) => setDeleteRecords(e.target.checked)}
            disabled={busy}
            className="mt-0.5 h-4 w-4"
          />
          <span>
            <span className="font-medium">
              Also archive the HubSpot records created by the test sync
            </span>{' '}
            (deals and line items — recommended). They move to HubSpot's trash
            with a 90-day recovery window.
          </span>
        </label>

        <p className="mt-3 text-xs text-ink-500">
          Contacts and products created from test data are kept — review them
          in HubSpot and delete any you don't want. After going live you'll be
          taken back to setup to connect your live Stripe account.
        </p>

        {error && (
          <p className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirm}
            disabled={busy}
          >
            {busy ? 'Going live…' : 'Go live'}
          </button>
        </div>
      </div>
    </div>
  );
}
